import classNames from 'classnames'
import { selectChannelsType, selectIsExpanded } from 'data/selectors'
import type { DetailsData } from 'data/types'
import { getExtendType, onExpand } from 'expanded/actions'
import type { Expanded } from 'expanded/types'
import { LoadingOverlay } from 'LoadingOverlay'
import type { ReactElement, ReactNode } from 'react'
import { Fragment } from 'react'
import { cloneElement, isValidElement } from 'react'
import { Children } from 'react'
import React, { memo, useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Carousel } from 'react-responsive-carousel'
import { SliderDots } from 'SliderDots'
import { selectLastReFetchTime } from 'store/reducers/lastReFetchTime'
import type { State } from 'store/types'
import { noop } from 'utils'

import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'

import { DetailsType } from './constants'
import DetailsBudget from './DetailsBudget'
import DetailsDefault from './DetailsDefault'
import DetailsECom from './DetailsECom'
import DetailsL4L from './DetailsL4L'
import DetailsMain from './DetailsMain'
import DetailsRetail from './DetailsRetail'
import type { ChannelSummaryType } from './types'
import useStyles from './useStyles'

export interface ChannelSummaryDetailedProps {
  channel?: string
  children: ReactNode
  className?: string
  countryId?: string
  data: DetailsData
  detailsType?: DetailsType
  fetchList?: () => void
  hideFlag?: boolean
  isLoading?: boolean
  label: string
  level?: number
  owner?: string
  saleType?: string
  type: ChannelSummaryType
}

const transitionProps = { unmountOnExit: true }

function ChannelSummaryDetailed({
  channel,
  children = null,
  className,
  countryId,
  data,
  detailsType,
  fetchList = noop,
  hideFlag = false,
  isLoading = false,
  label,
  level = 1,
  owner,
  saleType,
  type,
}: ChannelSummaryDetailedProps) {
  const dispatch = useDispatch()
  const classes = useStyles()
  const channelsType = useSelector(selectChannelsType)
  const isProd = useSelector((state: State) => state.system.isProd)
  const countryType = getExtendType({ owner, saleType })
  const expandType: keyof Expanded = type === 'countries' && channelsType !== 'countries' ? `countries-${countryType}` : type
  const expandedType = useSelector((state: State) => state.expanded[expandType])
  const shouldHideSummary = type === 'countries' ? expandedType !== countryId : expandedType !== label
  const lastReFetchTime = useSelector(selectLastReFetchTime)
  const isToggled = useSelector(selectIsExpanded({ channel, countryId, expandType, owner, saleType, type }))
  const onChange = useCallback(
    (_event: React.SyntheticEvent, expanded: boolean) => {
      dispatch(onExpand({ channelsType, countryId, expanded, owner, saleType, type, label }))
    },
    [dispatch, channelsType, countryId, owner, saleType, type, label],
  )
  const classesAccordionSummary = {
    content: classes.accordionSummaryContent,
    expanded: classes.accordionSummaryExpanded,
    root: classes.accordionSummaryRoot,
  }

  // Refetch lists
  useEffect(() => {
    if (isToggled && (channelsType === type || type === 'group')) fetchList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isToggled, channelsType, lastReFetchTime, type])
  const SUPPORTED_DETAILS = [
    Boolean(data.storesCurrentL4L || data.storesComparedL4L || !data.currency) && DetailsType.L4L,
    DetailsType.DEFAULT,
    !isProd && DetailsType.BUDGET,
    Boolean(data.storesCurrentNECom || data.storesComparedNECom || !data.currency) && DetailsType.RETAIL,
    Boolean(data.storesCurrentECom || data.storesComparedECom || !data.currency) && DetailsType.ECOM,
  ].filter(item => typeof item === 'number') as DetailsType[]

  const [currentSlide, setCurrentSlide] = useState(SUPPORTED_DETAILS.indexOf(detailsType || DetailsType.DEFAULT))
  const centerProps = level === 0 ? { centerMode: true, centerSlidePercentage: 85 } : {}

  const onChangeCarousel = (index: number) => {
    setCurrentSlide(index)
  }

  return (
    <Carousel
      className="control-dots-hide"
      emulateTouch
      onChange={onChangeCarousel}
      selectedItem={currentSlide}
      showArrows={false}
      showStatus={false}
      showThumbs={false}
      swipeScrollTolerance={70}
      {...centerProps}
    >
      {SUPPORTED_DETAILS.map((key, index) => {
        const childrenWithProps = Children.map(children as any, (child: ReactElement) => {
          if (child?.type === Fragment && child.props.children && isValidElement(child)) {
            return Children.map((child.props as any)?.children, (nestedChild: ReactElement) =>
              isValidElement(nestedChild) ? cloneElement(nestedChild, { detailsType: key } as any) : nestedChild,
            )
          }

          return child
        })

        return (
          <Box
            display={expandedType && shouldHideSummary ? 'none' : 'block'}
            className={classNames(classes.rootDetailed, className)}
            key={key}
          >
            <Accordion
              aria-label={`${type} summary`}
              role="treeitem"
              classes={{ root: classes.accordion, disabled: classes.disabled }}
              expanded={isToggled}
              disabled={isLoading}
              onChange={onChange}
              TransitionProps={transitionProps}
            >
              <AccordionSummary classes={classesAccordionSummary}>
                <Box width="100%" position="relative">
                  <LoadingOverlay isLoading={isLoading}>
                    <SliderDots
                      className={classes.sliderDots}
                      length={SUPPORTED_DETAILS.length}
                      selected={index}
                      onChange={setCurrentSlide}
                    />
                    <i className={classNames('fm fm-dropdownlist rotatable', classes.dropdownIcon, { rotate: isToggled })} />

                    <Grid container>
                      <DetailsMain countryId={countryId} data={data} detailsType={key} hideFlag={hideFlag} label={label} />
                      {key === DetailsType.DEFAULT && <DetailsDefault data={data} />}
                      {key === DetailsType.L4L && <DetailsL4L data={data} />}
                      {key === DetailsType.RETAIL && <DetailsRetail data={data} />}
                      {key === DetailsType.ECOM && <DetailsECom data={data} />}
                      {key === DetailsType.BUDGET && <DetailsBudget data={data} />}
                    </Grid>
                  </LoadingOverlay>
                </Box>
              </AccordionSummary>
              <AccordionDetails classes={{ root: classes.accordionDetails }}>{childrenWithProps}</AccordionDetails>
            </Accordion>
          </Box>
        )
      })}
    </Carousel>
  )
}

export default memo<ChannelSummaryDetailedProps>(ChannelSummaryDetailed)
